import type { Company, PurchaseOrderWithItems, Supplier } from '../types'

const fmtMoney = (n: number) =>
  n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

const fmtDate = (iso: string) => new Date(iso).toLocaleDateString('pt-BR')

function sanitizePhone(phone: string): string {
  return phone.replace(/\D/g, '')
}

export function formatPaymentTerms(supplier: Supplier | null): string | null {
  if (!supplier) return null
  const terms = [
    supplier.payment_term_1,
    supplier.payment_term_2,
    supplier.payment_term_3,
    supplier.payment_term_4,
    supplier.payment_term_5,
    supplier.payment_term_6,
  ].filter((t): t is number => t != null)
  if (terms.length === 0) return null
  return terms.join(' / ') + ' dias'
}

export function buildMailtoLink(opts: {
  order: PurchaseOrderWithItems
  supplier: Supplier
  company: Company
  pdfSignedUrl: string | null
}): string {
  const { order, supplier, company, pdfSignedUrl } = opts

  const tipoLabel = order.order_type === 'consignacao' ? 'Pedido de Consignação' : 'Pedido de Compra'
  const tipoLower = order.order_type === 'consignacao' ? 'pedido de consignação' : 'pedido de compra'

  const subject = `${tipoLabel} ${order.order_number} — ${company.name} — ${fmtDate(order.created_at)}`

  const lines: string[] = []
  lines.push(`Olá, segue ${tipoLower}.`)
  lines.push('')
  lines.push(`Nº Pedido: ${order.order_number}`)
  lines.push(`Fornecedor: ${supplier.name}`)
  lines.push(`Data: ${fmtDate(order.created_at)}`)
  lines.push(`Total: ${fmtMoney(order.total_value)}`)
  const terms = formatPaymentTerms(supplier)
  if (terms) lines.push(`Prazos de pagamento: ${terms}`)
  lines.push('')
  lines.push('Itens:')
  order.purchase_order_items.forEach((item, idx) => {
    lines.push(`${idx + 1}. ${item.product_name} — SKU ${item.sku} — Qtd ${item.quantity} — ${fmtMoney(item.unit_cost)}/un`)
  })
  lines.push('')
  if (pdfSignedUrl) {
    lines.push(`PDF do pedido (válido por 24h):`)
    lines.push(pdfSignedUrl)
    lines.push('')
  }
  lines.push(`Atenciosamente,`)
  lines.push(company.name)

  const body = encodeURIComponent(lines.join('\n'))
  const to = encodeURIComponent(supplier.email ?? '')

  return `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${body}`
}

export function buildGmailLink(opts: {
  order: PurchaseOrderWithItems
  supplier: Supplier
  company: Company
  pdfSignedUrl: string | null
}): string {
  const { order, supplier, company, pdfSignedUrl } = opts

  const tipoLabel = order.order_type === 'consignacao' ? 'Pedido de Consignação' : 'Pedido de Compra'
  const tipoLower = order.order_type === 'consignacao' ? 'pedido de consignação' : 'pedido de compra'

  const subject = `${tipoLabel} ${order.order_number} — ${company.name} — ${fmtDate(order.created_at)}`

  const lines: string[] = []
  lines.push(`Olá, segue ${tipoLower}.`)
  lines.push('')
  lines.push(`Nº Pedido: ${order.order_number}`)
  lines.push(`Fornecedor: ${supplier.name}`)
  lines.push(`Data: ${fmtDate(order.created_at)}`)
  lines.push(`Total: ${fmtMoney(order.total_value)}`)
  const terms = formatPaymentTerms(supplier)
  if (terms) lines.push(`Prazos de pagamento: ${terms}`)
  lines.push('')
  lines.push('Itens:')
  order.purchase_order_items.forEach((item, idx) => {
    lines.push(`${idx + 1}. ${item.product_name} — SKU ${item.sku} — Qtd ${item.quantity} — ${fmtMoney(item.unit_cost)}/un`)
  })
  lines.push('')
  if (pdfSignedUrl) {
    lines.push(`PDF do pedido (válido por 24h):`)
    lines.push(pdfSignedUrl)
    lines.push('')
  }
  lines.push(`Atenciosamente,`)
  lines.push(company.name)

  const params = new URLSearchParams({
    view: 'cm',
    fs: '1',
    to: supplier.email ?? '',
    su: subject,
    body: lines.join('\n'),
  })

  return `https://mail.google.com/mail/?${params.toString()}`
}

export function buildWhatsAppLink(opts: {
  order: PurchaseOrderWithItems
  supplier: Supplier
}): string {
  const { order, supplier } = opts
  const phone = supplier.phone ? sanitizePhone(supplier.phone) : ''

  const tipoLabel = order.order_type === 'consignacao' ? 'PEDIDO DE CONSIGNAÇÃO' : 'PEDIDO DE COMPRA'

  const lines: string[] = []
  lines.push(`*${tipoLabel}*`)
  lines.push(`Fornecedor: *${supplier.name}*`)
  lines.push(`Nº Pedido: ${order.order_number}`)
  lines.push(`Data: ${fmtDate(order.created_at)}`)
  lines.push('────────────────────')
  order.purchase_order_items.forEach((item, idx) => {
    lines.push(`${idx + 1}. ${item.product_name}`)
    lines.push(`   SKU: ${item.sku} | Qtd: ${item.quantity}`)
  })
  lines.push('────────────────────')
  lines.push(`*TOTAL: ${fmtMoney(order.total_value)}*`)
  const terms = formatPaymentTerms(supplier)
  if (terms) lines.push(`Prazos de pagamento: ${terms}`)
  lines.push('')
  lines.push('_Enviado via ax-kanban_')

  const text = encodeURIComponent(lines.join('\n'))
  const prefix = phone.length === 11 ? '55' : ''

  return `https://wa.me/${prefix}${phone}?text=${text}`
}

export type NegotiationMode = 'prazo' | 'parcelamento' | 'condicao'

/**
 * Fase 4 (assistida): monta um link wa.me com uma mensagem de NEGOCIAÇÃO de condição
 * comercial (não o pedido em si). O comprador dispara e envia manualmente — sem API
 * externa nem envio automático a terceiros. Modos: prazo (pede X dias), parcelamento
 * ou condição geral. `subtotal` dá contexto de volume ao fornecedor.
 */
export function buildWhatsAppNegotiationLink(opts: {
  supplier: Supplier
  subtotal: number
  prazoDiasDesejado?: number
  mode?: NegotiationMode
}): string {
  const { supplier, subtotal, prazoDiasDesejado = 45, mode = 'prazo' } = opts
  const phone = supplier.phone ? sanitizePhone(supplier.phone) : ''

  const lines: string[] = []
  lines.push('Bom dia! 👋')
  lines.push(`Temos interesse em um pedido de aprox. *${fmtMoney(subtotal)}* com a ${supplier.name}.`)
  if (mode === 'prazo') {
    lines.push(`Conseguimos fechar com prazo de pagamento de *${prazoDiasDesejado} dias*?`)
  } else if (mode === 'parcelamento') {
    lines.push('É possível *parcelar* o pagamento (ex.: 30/60/90 dias)?')
  } else {
    lines.push('Conseguimos negociar uma *condição comercial* melhor (prazo, parcelamento ou desconto)?')
  }
  lines.push('')
  lines.push('_Enviado via ax-kanban_')

  const text = encodeURIComponent(lines.join('\n'))
  const prefix = phone.length === 11 ? '55' : ''

  return `https://wa.me/${prefix}${phone}?text=${text}`
}
